import { useMemo } from 'react';
import { EnterpriseStructure, GuarantorDetailsFormType, guarantorDetailsFormSchema } from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { getEnumKey } from '@components/StepNavigator/helper';
import { useApplicationData } from '@provider';
import { APPLICATION_STEPS, GUARANTOR_DETAILS, pascalToCamelCase } from '@utils';
import { GuarantorSelection } from './GuarantorSelection';
import { useGuarantorDetailsSummary } from './hooks';

function GuarantorDetails() {
  const { TITLE } = GUARANTOR_DETAILS;
  const {
    OwnershipDetails: ownershipDetails,
    BusinessDetails: businessDetails,
    GuarantorDetails: guarantorDetails,
    quote,
  } = useApplicationData(['OwnershipDetails', 'BusinessDetails', 'GuarantorDetails', 'quote']);
  const financialProductId = quote?.financialProduct?.id ?? '';
  const structure = businessDetails?.enterprise?.structure;
  const structureProp = structure ? pascalToCamelCase(getEnumKey(structure, EnterpriseStructure)) : undefined;
  const completedContent = useGuarantorDetailsSummary();

  const directors = useMemo(() => {
    if (!ownershipDetails?.enterprise || !structure || !structureProp) return [];

    const enterpriseData = ownershipDetails.enterprise[structureProp];
    return enterpriseData?.directors ? enterpriseData.directors : [];
  }, [ownershipDetails, structure, structureProp]);

  if (!structure || !structureProp) {
    return null;
  }

  const defaultValues = guarantorDetails ?? {
    enterprise: {
      structure,
      [structureProp]: {
        directors: [],
      },
    },
  };

  return (
    <StepNavigator<GuarantorDetailsFormType>
      schema={guarantorDetailsFormSchema}
      name={APPLICATION_STEPS.GUARANTOR_DETAILS}
      title={TITLE}
      defaultValues={defaultValues}
      completedContent={completedContent}
    >
      {formControls => (
        <>
          <GuarantorSelection
            directors={directors}
            formControls={formControls}
            name={`enterprise.${structureProp}.directors`}
          />
          <input type="hidden" {...formControls.register('financialProductId')} value={financialProductId} />
          <input type="hidden" {...formControls.register('enterprise.structure')} value={structure} />
        </>
      )}
    </StepNavigator>
  );
}

export default GuarantorDetails;
